import { createContext, useContext } from 'react'
import { Outlet } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CurrentStaffAccount } from '@garage/shared'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { Spinner } from '@/ui'
import { LoginPage } from '@/pages/LoginPage.js'
import { ChangePasswordRequiredPage } from '@/pages/ChangePasswordRequiredPage.js'

interface AuthContextValue {
  account: CurrentStaffAccount
  /** Signs out on the server, wipes every cached query (this was a shared
   *  workshop PC a moment ago), and lets AuthGate's own query re-fetch into
   *  the logged-out state — no page reload needed. */
  signOut: () => void
  signingOut: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * The signed-in account and a sign-out action. Only callable from inside the
 * authenticated branch of the tree — everything rendered under `AuthGate`'s
 * `<Outlet/>` is, which in practice means everything under `<AppShell/>`.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth() called outside AuthGate')
  return ctx
}

/**
 * Layout route guarding every staff-facing page.
 *
 * Deliberately NOT wrapped around `/p/:token` (see App.tsx): the customer
 * portal listener serves this exact same built JS bundle to reach that one
 * route, and a customer must never be shown a staff login screen. The split
 * happens in the route tree, not behind a build-time flag, so there is only
 * one bundle to reason about.
 *
 * Three states, in order: still finding out (spinner), no valid session
 * (LoginPage), signed in but must set a real password before anything else
 * (ChangePasswordRequiredPage). Only past all three does the real app render.
 */
export function AuthGate() {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: qk.authMe,
    queryFn: () => api.auth.me(),
    retry: false,
    staleTime: 60 * 1000,
  })

  const signOutMutation = useMutation({
    mutationFn: () => api.auth.logout(),
    onSettled: () => {
      // Clears business data cached from the last session, not just the auth
      // query — a "sign out" on a shared workshop PC should leave nothing
      // behind for the next person to scroll back into.
      queryClient.clear()
    },
  })

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="md" />
      </div>
    )
  }

  // A 401 here just means "not signed in" — the ordinary case on a fresh
  // visit. Any other failure (network hiccup, 500) shows the same screen:
  // there is nothing else useful to render without a confirmed session.
  if (query.isError || !query.data) {
    return <LoginPage onSignedIn={() => queryClient.invalidateQueries({ queryKey: qk.authMe })} />
  }

  if (query.data.mustChangePassword) {
    return (
      <ChangePasswordRequiredPage
        account={query.data}
        onChanged={() => queryClient.invalidateQueries({ queryKey: qk.authMe })}
      />
    )
  }

  return (
    <AuthContext.Provider
      value={{
        account: query.data,
        signOut: () => signOutMutation.mutate(),
        signingOut: signOutMutation.isPending,
      }}
    >
      <Outlet />
    </AuthContext.Provider>
  )
}
